import { ActionIcon, Box, Card, Select, Stack, Text } from "@mantine/core";
import { DateInput, TimePicker } from "@mantine/dates";
import { IMeeting } from "@mctypes/meeting.types";
import { IconTrash } from "@tabler/icons-react";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { ConfirmDialog, FailedAlert } from "../../../../../components";
import McInputNew from "../../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../../components/McInput/McTextAreaNew";
import { McMantineUserSelect } from "../../../../../components/McMantineUserSelect/McMantineUserSelect";
import { getProjectIdFromUrl } from "../../../../utils";
import { useGetProjectByProjectIdIssueboardQuery } from "../../../../../../openapi";
import { useUpdateMeetingMutation } from "../../meetingRTK";

const toDateInputValue = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const parsed = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toYyyyMmDd = (value: Date | string | null): string => {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const MeetingInfo: React.FC<{ singleMeeting: IMeeting.MeetingPopulated }> = ({ singleMeeting }) => {
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);

  const { data: issueBoards = [] } = useGetProjectByProjectIdIssueboardQuery({
    projectId: projectId,
  });

  const onDelete = () => {
    updateMeeting({
      meetingId: singleMeeting._id,
      projectId,
      attribute: "isDeleted",
      value: true,
    });
    history.push(`/project/${projectId}/meetings`);
  };

  const [updateMeeting, { isError: updateMeetingError }] = useUpdateMeetingMutation();

  const onChange = (attribute: keyof IMeeting.MeetingPopulated, value: string | boolean | Date | null) => {
    updateMeeting({
      meetingId: singleMeeting._id,
      projectId,
      attribute,
      value,
    });
  };

  const participantUsers = useMemo(
    () =>
      (singleMeeting?.participants as IMeeting.ParticipantsPopulated[])?.map((participant) => participant.user) ?? [],
    [singleMeeting?.participants],
  );

  const participantComboboxData = useMemo(
    () => participantUsers.map((user) => ({ value: user._id, label: user.name })),
    [participantUsers],
  );

  const issueBoardSelectData = useMemo(
    () => issueBoards.map((board) => ({ value: board._id, label: board.name })),
    [issueBoards],
  );

  const defaultIssueBoardValue =
    singleMeeting.defaultIssueBoard == null
      ? null
      : typeof singleMeeting.defaultIssueBoard === "string"
      ? singleMeeting.defaultIssueBoard
      : singleMeeting.defaultIssueBoard._id;

  return (
    <Box w={500} p="lg">
      <Card p="sm" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
        <Stack gap="md">
          {updateMeetingError === true && <FailedAlert />}

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Velg dato for møte
            </Text>
            <DateInput
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              valueFormat="DD.MM.YYYY"
              value={toDateInputValue(singleMeeting.dateOfMeeting)}
              onChange={(value) => onChange("dateOfMeeting", toYyyyMmDd(value))}
              placeholder="Skriv inn dato for møtet"
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Starttid for møte
            </Text>
            <TimePicker
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              format="24h"
              withSeconds={false}
              withDropdown
              value={singleMeeting.timeStart ?? ""}
              onChange={(value) => {
                if ((singleMeeting.timeStart ?? "") === value) return;
                onChange("timeStart", value);
              }}
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Møtelokasjon
            </Text>
            <McInputNew
              type="text"
              onBlur={(t) => onChange("location", t.target.value as any)}
              value={singleMeeting.location}
              placeholder="Skriv inn lokasjon for møtet"
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Eier for møte
            </Text>
            <McMantineUserSelect
              data={participantComboboxData}
              value={
                singleMeeting.owner == null
                  ? null
                  : typeof singleMeeting.owner === "string"
                  ? singleMeeting.owner
                  : singleMeeting.owner._id
              }
              onChange={(userId) => onChange("owner", userId)}
            />
          </Stack>
          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Referent
            </Text>
            <McMantineUserSelect
              data={participantComboboxData}
              value={
                singleMeeting.referent == null
                  ? null
                  : typeof singleMeeting.referent === "string"
                  ? singleMeeting.referent
                  : singleMeeting.referent._id
              }
              onChange={(userId) => onChange("referent", userId)}
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Navn på møte
            </Text>
            <McInputNew
              type="text"
              onBlur={(t) => onChange("name", t.target.value as any)}
              value={singleMeeting.name}
              placeholder="Skriv inn navn på møtet"
            />
          </Stack>
          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Hensikten med møte
            </Text>
            <McTextAreaNew
              autosize
              onBlur={(t) => onChange("description", t.target.value as any)}
              value={singleMeeting.description}
              spellCheck={false}
              placeholder="Skriv inn beskrivelse"
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Standard saksregister
            </Text>
            <Select
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              searchable
              clearable
              disabled={issueBoards.length === 0}
              value={defaultIssueBoardValue}
              data={issueBoardSelectData}
              placeholder="Velg saksregister"
              onChange={(value) => onChange("defaultIssueBoard", value)}
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Dokumentkode
            </Text>
            <McInputNew
              type="text"
              onBlur={(t) => onChange("documentCode", t.target.value as any)}
              value={singleMeeting.documentCode}
              placeholder="Skriv inn dokumentkode"
            />
          </Stack>
          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Tilgjengelighet
            </Text>
            <McInputNew
              type="text"
              onBlur={(t) => onChange("availability", t.target.value as any)}
              value={singleMeeting.availability}
              placeholder="Skriv inn tilgjengelighet"
            />
          </Stack>

          <ConfirmDialog title="Slette møtet?" message="Er du sikker på at du vil slette møtet?" confirmCb={onDelete}>
            <ActionIcon color="red" variant="subtle" aria-label="Slett møte">
              <IconTrash size={18} />
            </ActionIcon>
          </ConfirmDialog>
        </Stack>
      </Card>
    </Box>
  );
};

export default MeetingInfo;


